import axios from 'axios'
import requests from '../../request/list/matchmaking_v2'

const EVENTS = new Set(['QUESTION_PRESENTED', 'QUESTION_CONTRACT_ERROR', 'CONTINUE_CLICKED',
  'VALIDATION_FAILED', 'ANSWER_RESULT', 'QUESTION_RETRY', 'PREV_RESULT', 'FINALIZE_RESULT',
  'SCREENING_LOAD_RESULT', 'RESTART_CLICKED'])
const CODES = new Set(['NONE', 'NETWORK_ERROR', 'REQUEST_FAILED', 'AUTH_REQUIRED', 'RATE_LIMITED',
  'SCHEMA_INVALID', 'INTAKE_NOT_FOUND', 'INTAKE_FINALIZED', 'INTAKE_TERMINAL', 'LEDGER_REPLAY_FAILED',
  'VERSION_STALE', 'VERSION_UNKNOWN', 'FIELD_REQUIRED', 'QUESTION_OPTIONS_UNAVAILABLE',
  'QUESTION_PRESENTATION_UNAVAILABLE', 'QUESTION_SCALE_UNAVAILABLE', 'QUESTION_TYPE_UNSUPPORTED',
  'QUESTION_RESPONSE_INVALID'])
const recent = new Map()
let inFlight = 0
const queue = []

function pump() {
  while (inFlight < 2 && queue.length) {
    const job = queue.shift()
    inFlight++
    Promise.resolve().then(job.send).then(() => job.resolve(true), () => job.resolve(false))
      .finally(() => {inFlight--; pump()})
  }
}

function eventId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const n = Math.floor(Math.random() * 16)
    return (c === 'x' ? n : ((n & 3) | 8)).toString(16)
  })
}

export function diagnosticErrorCode(error) {
  const code = error && error.code
  if (CODES.has(code)) return code
  return error && !error.response && (error.request || error.isAxiosError
    || ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(code)) ? 'NETWORK_ERROR' : 'REQUEST_FAILED'
}

function buildId() {
  if (typeof document === 'undefined') return 'unknown'
  const scripts = Array.from(document.scripts || [])
  for (const script of scripts) {
    const m = String(script.src || '').match(/\/app\.([a-f0-9]{8,40})\.js(?:$|[?#])/)
    if (m) return m[1]
  }
  return 'unknown'
}

/** Best-effort first-party technical metadata. Never accepts an answer/payload. */
export function recordScreeningDiagnostic(session, event, details = {}) {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined'
        || !/^(true|1)$/i.test(String(process.env.VUE_APP_MATCHMAKING_V2_API || '')) || !EVENTS.has(event)) return Promise.resolve(false)
    const id = Number(session && session.intake_id)
    if (!Number.isSafeInteger(id) || id <= 0 || queue.length >= 32) return Promise.resolve(false)
    const token = localStorage.getItem('token')
    if (!token) return Promise.resolve(false)
    const qid = details.questionId || (session.next_question && session.next_question.question_id) || null
    if (qid !== null && !/^[A-Z][A-Z0-9_]{0,63}$/.test(qid)) return Promise.resolve(false)
    const status = ['observed', 'requested', 'success', 'failure'].includes(details.status) ? details.status : 'observed'
    const code = CODES.has(details.code) ? details.code : 'NONE'
    const revision = Number(session.write_revision || 0)
    if (!Number.isSafeInteger(revision) || revision < 0 || revision > 2147483647) return Promise.resolve(false)
    const key = [id, revision, qid, event, status, code].join(':')
    const now = Date.now()
    if (recent.has(key) && now - recent.get(key) < 500) return Promise.resolve(false)
    const route = requests.find(r => r.name === 'getScreeningSession')
    if (!route) return Promise.resolve(false)
    const body = {event_id: eventId(), event, question_id: qid, client_revision: revision,
      status, code, build_id: buildId()}
    if (Number.isInteger(details.controlCount) && details.controlCount >= 0 && details.controlCount <= 1001) body.control_count = details.controlCount
    if (Number.isInteger(details.httpStatus) && details.httpStatus >= 0 && details.httpStatus <= 599) body.http_status = details.httpStatus
    recent.set(key, now)
    if (recent.size > 256) recent.delete(recent.keys().next().value)
    // Bypass the shared toast/backoff layer: diagnostics must not affect the journey.
    return new Promise(resolve => {
      queue.push({resolve, send: () => axios.post(route.path.replace(':intakeId', String(id)) + '/diagnostics', body,
        {headers: {Authorization: 'Bearer ' + token}, timeout: 2500})})
      pump()
    })
  } catch (ignore) {
    inFlight = Math.max(0, inFlight)
    return Promise.resolve(false)
  }
}
